import type { AutocompleteSuggestion } from '@klareospace/pharos-lib-types';
import type { FieldValues, Path } from 'react-hook-form';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressSuggestionFormInput {
  city: string;
  country: string;
  houseNumber: string;
  postalCode: string;
  street: string;
}

export interface AppliedAddressSuggestion {
  address: AddressSuggestionFormInput;
  coords: { lat: number; lng: number } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type SuggestionAddress = NonNullable<AutocompleteSuggestion['address']>;

function extractAddressFields(suggestion: AutocompleteSuggestion): AddressSuggestionFormInput {
  const address: Partial<SuggestionAddress> = suggestion.address ?? {};
  return {
    city: address.city ?? '',
    country: address.countryCode ?? '',
    houseNumber: address.houseNumber ?? '',
    postalCode: address.postalCode ?? '',
    street: address.street ?? '',
  };
}

function extractCoords(suggestion: AutocompleteSuggestion): { lat: number; lng: number } | null {
  const { position } = suggestion;
  return position ? { lat: position.latitude, lng: position.longitude } : null;
}

// ─── Utils ────────────────────────────────────────────────────────────────────

export function mapSuggestionToAddressFormInput(suggestion: AutocompleteSuggestion): AppliedAddressSuggestion {
  return { address: extractAddressFields(suggestion), coords: extractCoords(suggestion) };
}

/**
 * Writes every address field from a suggestion onto the form, unconditionally — a field the
 * suggestion doesn't carry is cleared (set to ''), never left holding a stale prior value.
 */
export function applySuggestionToAddressForm<TFieldValues extends FieldValues>(
  suggestion: AutocompleteSuggestion,
  setValue: (field: Path<TFieldValues>, value: unknown) => void,
): AppliedAddressSuggestion {
  const result = mapSuggestionToAddressFormInput(suggestion);
  setValue('address.city' as Path<TFieldValues>, result.address.city);
  setValue('address.country' as Path<TFieldValues>, result.address.country);
  setValue('address.houseNumber' as Path<TFieldValues>, result.address.houseNumber);
  setValue('address.postalCode' as Path<TFieldValues>, result.address.postalCode);
  setValue('address.street' as Path<TFieldValues>, result.address.street);
  return result;
}
